import Spinner from "../../shared/UxbiSpinner";
import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Tab,
  Tabs,
} from "react-bootstrap";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import RemoteSelect from "../../components/common/RemoteSelect";
import Field from "../../components/common/FormField";
import {
  listarCatalogo,
  obtenerCatalogo,
  listarEmpresas,
  obtenerEmpresa,
  listarAgentesVentas,
  listarAgentesOperaciones,
  obtenerAgenteOperaciones,
  obtenerAgenteVentas,
  listarGruposClientes,
  obtenerGrupoCliente,
} from "../../api/catalogos";
import {
  crearCliente,
  actualizarCliente,
  obtenerCliente,
} from "../../api/clientes";
import { extractCollection } from "../../components/documentos/documentoUtils";
import {
  emptyClient,
  emptyAddress,
  emptyContact,
  normalizeClientResponse,
  validateClient,
  buildClientPayload,
  addressStarted,
} from "./clienteForm";
import AddressFields from "./AddressFields";

const textFields = [
  ["nombre", "Nombre", 6, 150, true],
  ["clave", "Clave", 3, 10],
  ["razon_social", "Razón social", 6, 150],
  ["alias_1", "Alias 1", 3, 45],
  ["alias_2", "Alias 2", 3, 45],
  ["rfc", "RFC", 3, 13],
  ["cp_fiscal", "C.P. fiscal", 3, 5, true],
  ["registro_identidad_tributaria", "Registro de identidad fiscal", 6, 20],
  ["nombre_contacto", "Nombre de contacto", 4, 100],
  ["email", "Correo electrónico", 4, 100],
  ["telefono", "Teléfono", 4, 30],
  ["telefono2", "Teléfono 2", 4, 30],
  ["dir_internet", "Dirección de Internet", 8, 60],
];
const relations = [
  [
    "id_agente_ventas",
    "Agente de ventas",
    listarAgentesVentas,
    obtenerAgenteVentas,
    true,
  ],
  [
    "id_agente_operaciones",
    "Agente de operaciones",
    listarAgentesOperaciones,
    obtenerAgenteOperaciones,
  ],
  [
    "id_fuente",
    "Fuente",
    (params) => listarCatalogo("fuentes", params),
    (id) => obtenerCatalogo("fuentes", id),
  ],
  [
    "id_agente_ventas_2",
    "Agente de ventas 2",
    listarAgentesVentas,
    obtenerAgenteVentas,
  ],
  ["id_empresa", "Empresa", listarEmpresas, obtenerEmpresa],
  [
    "id_zona",
    "Zona",
    (params) => listarCatalogo("zonas", params),
    (id) => obtenerCatalogo("zonas", id),
  ],
  [
    "id_clientes_grupo",
    "Grupo de clientes",
    listarGruposClientes,
    obtenerGrupoCliente,
    true,
  ],
  [
    "id_agente_cxc",
    "Agente de cuentas por cobrar",
    (params) =>
      listarCatalogo("usuarios", {
        ...params,
        searchProperty: "nombre",
      }),
    (id) => obtenerCatalogo("usuarios", id),
  ],
  [
    "id_clientes_tipo",
    "Tipo de cliente",
    (params) => listarCatalogo("clientes-tipos", params),
    (id) => obtenerCatalogo("clientes-tipos", id),
    true,
  ],
];
const sats = [
  ["metodo_pago", "Método de pago", "metodos_pago"],
  ["forma_pago", "Forma de pago", "formas_pago"],
  ["uso_comprobante", "Uso de CFDI", "usos_comprobantes"],
  ["regimen_fiscal", "Régimen fiscal", "regimenes_fiscales"],
];
const labelOption = (option) =>
  option.nombre ||
  option.descripcion ||
  option.razon_social ||
  option.clave ||
  `#${option.id}`;
const allowed = (name) => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if ((user.roles || []).some((role) => role.name === "ADMIN")) return true;
    return JSON.parse(localStorage.getItem("permissions") || "[]").some(
      (permission) =>
        (typeof permission === "string" ? permission : permission.name) ===
        name,
    );
  } catch {
    return false;
  }
};
export default function ClienteFormModal({
  show,
  clienteId,
  readOnly = false,
  onHide,
  onSaved,
}) {
  const [form, setForm] = useState(emptyClient);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sat, setSat] = useState({});
  const [activeTab, setActiveTab] = useState("generales");
  useEffect(() => {
    if (!show) return;
    let active = true;
    setErrors({});
    setLoadError("");
    setLoading(false);
    setActiveTab("generales");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!clienteId)
      setForm({ ...emptyClient(), id_empresa: user.id_empresa || "" });
    else {
      setLoading(true);
      obtenerCliente(clienteId)
        .then((response) => {
          if (active) setForm(normalizeClientResponse(response));
        })
        .catch((error) => {
          if (active) setLoadError(error.message);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }
    Promise.all(
      sats.map(async ([name, , endpoint]) => [
        name,
        extractCollection(
          await listarCatalogo(`facturacion/catalogos/sat/${endpoint}`, {
            limit: 1000,
          }),
        ),
      ]),
    )
      .then((entries) => {
        if (active) setSat(Object.fromEntries(entries));
      })
      .catch((error) => toast.error(`Catálogos SAT: ${error.message}`));
    return () => {
      active = false;
    };
  }, [show, clienteId, revision]);
  const change = (name, value) =>
    setForm((current) => ({ ...current, [name]: value }));
  const updateRow = (name, index, key, value) =>
    setForm((current) => ({
      ...current,
      [name]: current[name].map((row, i) =>
        i === index ? { ...row, [key]: value } : row,
      ),
    }));
  const add = (name, row) =>
    setForm((current) => ({ ...current, [name]: [...current[name], row] }));
  const remove = (name, index) =>
    setForm((current) => ({
      ...current,
      [name]: current[name].filter((_, i) => i !== index),
    }));
  const field = (name, label, extra = {}) => (
    <Field
      key={name}
      {...{ name, label }}
      value={form[name]}
      onChange={change}
      disabled={readOnly || saving}
      error={errors[name]}
      {...extra}
    />
  );
  const save = async (event) => {
    event.preventDefault();
    const invalid = validateClient(form);
    setErrors(invalid);
    if (Object.keys(invalid).length) {
      const first = Object.keys(invalid)[0];
      setActiveTab(
        first.startsWith("direcciones_entrega")
          ? "entregas"
          : first.startsWith("contactos")
            ? "contactos"
            : "generales",
      );
      toast.error(Object.values(invalid)[0]);
      return;
    }
    setSaving(true);
    try {
      const payload = buildClientPayload(form);
      if (clienteId) await actualizarCliente(clienteId, payload);
      else await crearCliente(payload);
      toast.success("Cliente guardado correctamente.");
      onSaved();
    } catch (error) {
      setErrors(error.body?.errors || error.body?.errores || {});
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onHide}
      className="catalog-full-modal"
      size="xl"
      scrollable
      backdrop="static"
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          {readOnly
            ? "Detalle del cliente"
            : clienteId
              ? "Editar cliente"
              : "Nuevo cliente"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={save} noValidate>
        <Modal.Body>
          {loadError ? (
            <div className="alert alert-danger" role="alert">
              {loadError}{" "}
              <Button variant="link" onClick={() => setRevision((v) => v + 1)}>
                Volver a cargar
              </Button>
            </div>
          ) : loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <Tabs
              activeKey={activeTab}
              onSelect={setActiveTab}
              className="mb-4 catalog-form-tabs"
            >
              <Tab eventKey="generales" title="Datos generales">
                <Row className="g-3">
                  {textFields.map(([name, label, md, maxLength, required]) =>
                    field(name, label, { md, maxLength, required }),
                  )}
                  {relations.map(
                    ([name, label, loadOptions, loadById, required]) => (
                      <Col md={4} key={name}>
                        <RemoteSelect
                          {...{ name, label, loadOptions, required }}
                          loadById={(id) => {
                            const relation = {
                              id_agente_ventas: "agente_ventas",
                              id_agente_cxc: "agente_cxc",
                              id_agente_ventas_2: "agente_ventas2",
                              id_agente_operaciones: "agente_operaciones",
                            }[name];
                            const existing = relation && form[relation];
                            return existing &&
                              String(existing.id) === String(id)
                              ? Promise.resolve({ data: existing })
                              : loadById(id);
                          }}
                          value={form[name]}
                          onChange={change}
                          getOptionLabel={labelOption}
                          error={errors[name]}
                          disabled={
                            readOnly ||
                            (name === "id_empresa" &&
                              Boolean(
                                JSON.parse(localStorage.getItem("user") || "{}")
                                  .id_empresa,
                              ))
                          }
                        />
                      </Col>
                    ),
                  )}
                  {field("comentarios", "Comentarios", {
                    type: "textarea",
                    md: 12,
                    maxLength: 300,
                  })}
                </Row>
                <h6 className="mt-4">Dirección de facturación</h6>
                <p className="text-secondary small">
                  Opcional. Al capturar una dirección, completa los campos
                  marcados.
                </p>
                <AddressFields
                  value={form.direccion_facturacion}
                  onChange={(name, value) =>
                    change("direccion_facturacion", {
                      ...form.direccion_facturacion,
                      [name]: value,
                    })
                  }
                  disabled={readOnly || saving}
                  required={addressStarted(form.direccion_facturacion)}
                  errors={Object.fromEntries(
                    Object.entries(errors)
                      .filter(([key]) =>
                        key.startsWith("direccion_facturacion."),
                      )
                      .map(([key, value]) => [key.split(".").pop(), value]),
                  )}
                />
              </Tab>
              <Tab eventKey="comerciales" title="Comerciales y fiscales">
                <Row className="g-3">
                  {field("dias_credito", "Días de crédito", {
                    type: "number",
                    min: 0,
                    disabled: readOnly || !allowed("editar_dias_credito"),
                  })}
                  {field("limite_credito", "Límite de crédito", {
                    type: "number",
                    min: 0,
                    disabled: readOnly || !allowed("editar_limite_credito"),
                  })}
                  {field("iva", "IVA (%)", { type: "number", min: 0 })}
                  {field("porcentaje_cliente", "Porcentaje cliente (%)", {
                    type: "number",
                    min: 0,
                    max: 100,
                  })}
                  {sats.map(([name, label]) =>
                    field(`clave_${name}_sat`, label, {
                      options: (sat[name]?.some(
                        (row) =>
                          String(row.clave) ===
                          String(form[`clave_${name}_sat`]),
                      ) || !form[`clave_${name}_sat`]
                        ? sat[name] || []
                        : [
                            {
                              clave: form[`clave_${name}_sat`],
                              descripcion: form[`${name}_sat`],
                            },
                            ...(sat[name] || []),
                          ]
                      ).map((row) => ({
                        value: row.clave,
                        label: `${row.clave} — ${row.descripcion || row.nombre || ""}`,
                      })),
                      onChange: (_, value) => {
                        const row = (sat[name] || []).find(
                          (item) => String(item.clave) === String(value),
                        );
                        setForm((current) => ({
                          ...current,
                          [`clave_${name}_sat`]: value,
                          [`${name}_sat`]:
                            row?.descripcion || row?.nombre || "",
                        }));
                      },
                    }),
                  )}
                </Row>
                <Row className="g-3 mt-3">
                  {[
                    ["es_certificador", "Es certificador"],
                    ["enviar_saldos", "Enviar antigüedad de saldos"],
                    ["bloqueado", "Cliente bloqueado"],
                    [
                      "permitir_sin_limite",
                      "Permitir venta sin límite de crédito",
                    ],
                    ["cheque_posfechado", "Pago con cheque posfechado"],
                    ["enviar_factura", "Enviar factura por correo"],
                    ["exportacion", "Cliente de exportación"],
                    ["venta_sin_saldo", "Permitir venta sin validar saldo"],
                  ].map(([name, label]) =>
                    field(name, label, { type: "checkbox", md: 6 }),
                  )}
                </Row>
                <h6 className="mt-4">Días de envío de antigüedad de saldos</h6>
                <div className="d-flex flex-wrap gap-3">
                  {Array.from({ length: 31 }, (_, index) =>
                    String(index + 1),
                  ).map((day) => (
                    <Form.Check
                      key={day}
                      id={`dia-envio-${day}`}
                      label={day}
                      disabled={readOnly || saving}
                      checked={String(form.dias_envio || "")
                        .split("|")
                        .includes(day)}
                      onChange={(event) => {
                        const days = String(form.dias_envio || "")
                          .split("|")
                          .filter(Boolean)
                          .filter((value) => value !== day);
                        if (event.target.checked) days.push(day);
                        change(
                          "dias_envio",
                          days.length
                            ? `|${days.sort((a, b) => Number(a) - Number(b)).join("|")}|`
                            : "",
                        );
                      }}
                    />
                  ))}
                </div>
              </Tab>
              <Tab
                eventKey="entregas"
                title={`Entregas (${form.direcciones_entrega.length})`}
              >
                {form.direcciones_entrega.map((address, index) => (
                  <Card key={address.id || index} className="mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between mb-3">
                        <h6>Dirección {index + 1}</h6>
                        {!readOnly && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => remove("direcciones_entrega", index)}
                          >
                            <Trash2 size={16} /> Quitar
                          </Button>
                        )}
                      </div>
                      <AddressFields
                        value={address}
                        required
                        disabled={readOnly || saving}
                        onChange={(name, value) =>
                          updateRow("direcciones_entrega", index, name, value)
                        }
                        errors={Object.fromEntries(
                          Object.entries(errors)
                            .filter(([key]) =>
                              key.startsWith(`direcciones_entrega.${index}.`),
                            )
                            .map(([key, value]) => [
                              key.split(".").pop(),
                              value,
                            ]),
                        )}
                      />
                    </Card.Body>
                  </Card>
                ))}
                {!readOnly && (
                  <Button
                    variant="outline-primary"
                    onClick={() => add("direcciones_entrega", emptyAddress())}
                  >
                    <Plus size={16} /> Agregar dirección
                  </Button>
                )}
              </Tab>
              <Tab
                eventKey="contactos"
                title={`Contactos (${form.contactos.length})`}
              >
                {form.contactos.map((contact, index) => (
                  <Card key={contact.id || index} className="mb-3">
                    <Card.Body>
                      <Row className="g-3">
                        {[
                          ["nombre", "Nombre"],
                          ["apellido_paterno", "Apellido paterno"],
                          ["apellido_materno", "Apellido materno"],
                          ["departamento", "Departamento"],
                          ["puesto", "Puesto"],
                          ["email", "Correo electrónico"],
                          ["telefono", "Teléfono"],
                          ["extension", "Extensión"],
                        ].map(([name, label]) => (
                          <Field
                            key={name}
                            {...{ name, label }}
                            value={contact[name]}
                            onChange={(_, value) =>
                              updateRow("contactos", index, name, value)
                            }
                            disabled={readOnly || saving}
                            error={errors[`contactos.${index}.${name}`]}
                            required={name === "nombre"}
                          />
                        ))}
                        <Field
                          name="enviar_correo"
                          label="Enviar correo"
                          type="checkbox"
                          value={contact.enviar_correo}
                          disabled={readOnly || saving}
                          onChange={(_, value) =>
                            updateRow(
                              "contactos",
                              index,
                              "enviar_correo",
                              value,
                            )
                          }
                        />
                      </Row>
                      {!readOnly && (
                        <Button
                          className="mt-3"
                          variant="outline-danger"
                          size="sm"
                          onClick={() => remove("contactos", index)}
                        >
                          <Trash2 size={16} /> Quitar contacto
                        </Button>
                      )}
                    </Card.Body>
                  </Card>
                ))}
                {!readOnly && (
                  <Button
                    variant="outline-primary"
                    onClick={() => add("contactos", emptyContact())}
                  >
                    <Plus size={16} /> Agregar contacto
                  </Button>
                )}
              </Tab>
              <Tab eventKey="relaciones" title="Métodos, productos y addendas">
                <ClientRelations
                  {...{ form, readOnly, add, remove, updateRow, saving }}
                />
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={saving}
          >
            Cerrar
          </Button>
          {!readOnly && (
            <Button type="submit" disabled={saving || loading || !!loadError}>
              {saving ? <Spinner size="sm" /> : <Save size={16} />} Guardar
              cliente
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
function ClientRelations({ form, readOnly, add, remove, updateRow, saving }) {
  const sections = [
    {
      name: "metodos_pago",
      label: "Métodos de pago",
      endpoint: "metodos-pago",
      key: "id_metodo_pago",
      fields: [
        ["cuenta", "Cuenta"],
        ["banco", "Banco"],
      ],
      defaults: { activo: true },
    },
    {
      name: "productos",
      label: "Productos del cliente",
      endpoint: "productos",
      key: "id_producto",
      fields: [
        ["descripcion", "Descripción"],
        ["precio", "Precio", "number"],
        ["comision", "Comisión", "number"],
        ["comision_pct", "Comisión (%)", "number"],
        ["vigencia", "Vigencia", "date"],
      ],
      defaults: { precio: 0, comision: 0, comision_pct: 0 },
    },
    {
      name: "addendas",
      label: "Addendas",
      endpoint: "addendas",
      key: "id_addenda",
      fields: [],
      defaults: {},
    },
  ];
  return sections.map((section) => (
    <section key={section.name} className="mb-4">
      <h6>{section.label}</h6>
      {form[section.name].map((row, index) => (
        <Card className="mb-2" key={index}>
          <Card.Body>
            <Row className="g-3">
              <Col md={4}>
                <RemoteSelect
                  name={section.key}
                  label="Selecciona"
                  value={row[section.key]}
                  disabled={readOnly || saving}
                  onChange={(_, value) =>
                    updateRow(section.name, index, section.key, value)
                  }
                  loadOptions={(params) =>
                    listarCatalogo(section.endpoint, {
                      ...params,
                      searchProperty:
                        section.endpoint === "metodos-pago"
                          ? "metodo_pago"
                          : "descripcion",
                    })
                  }
                  loadById={(id) => obtenerCatalogo(section.endpoint, id)}
                  getOptionLabel={(option) =>
                    option.metodo_pago || labelOption(option)
                  }
                />
              </Col>
              {section.fields.map(([name, label, type]) => (
                <Field
                  key={name}
                  {...{ name, label, type }}
                  value={row[name]}
                  disabled={readOnly || saving}
                  onChange={(_, value) =>
                    updateRow(section.name, index, name, value)
                  }
                />
              ))}
              {section.name === "metodos_pago" && (
                <Field
                  type="checkbox"
                  name="activo"
                  label="Activo"
                  value={row.activo}
                  disabled={readOnly || saving}
                  onChange={(_, value) =>
                    updateRow(section.name, index, "activo", value)
                  }
                />
              )}
            </Row>
            {!readOnly && (
              <Button
                variant="outline-danger"
                size="sm"
                className="mt-3"
                onClick={() => remove(section.name, index)}
              >
                <Trash2 size={16} /> Quitar
              </Button>
            )}
          </Card.Body>
        </Card>
      ))}
      {!readOnly && (
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() =>
            add(section.name, { [section.key]: "", ...section.defaults })
          }
        >
          <Plus size={16} /> Agregar
        </Button>
      )}
    </section>
  ));
}
